import { useContext, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Avatar, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import { CalendarMonth, EventAvailable, MailOutline, EventNote, Person } from '@mui/icons-material';
import AuthContext from '../contexts/AuthContext';
import { useUnreadMessages } from '../hooks/useUnreadMessages';

const TAB_ITEMS = [
  { path: '/my-shifts', label: 'Docházka', shortLabel: 'Docházka', icon: CalendarMonth },
  { path: '/available-shifts', label: 'Směny', shortLabel: 'Směny', icon: EventAvailable },
  {
    path: '/shift-allocations',
    label: 'Plán',
    shortLabel: 'Plán',
    icon: EventNote,
    requiresShiftPermission: true,
  },
  { path: '/messages', label: 'Zprávy', shortLabel: 'Zprávy', icon: MailOutline, showUnread: true },
];

const ROUTE_TITLES = {
  '/my-shifts': 'Docházka',
  '/available-shifts': 'Směny',
  '/messages': 'Zprávy',
  '/shift-allocations': 'Plán',
  '/profile': 'Profil',
};

const MobileAppShell = ({ children, title: titleOverride }) => {
  const history = useHistory();
  const location = useLocation();
  const { featurePermissions, isAdmin, logout } = useContext(AuthContext);
  const { unreadCount } = useUnreadMessages();
  const [menuAnchor, setMenuAnchor] = useState(null);

  const canReadShiftAllocations =
    isAdmin ||
    featurePermissions?.find((x) => x.feature === 'shiftAllocations')?.permissions?.canReadAny;

  const visibleTabs = TAB_ITEMS.filter(
    (item) => !item.requiresShiftPermission || canReadShiftAllocations
  );

  const activeTabPath = visibleTabs.find((tab) => location.pathname.startsWith(tab.path))?.path;

  const title =
    titleOverride || ROUTE_TITLES[location.pathname] || ROUTE_TITLES[activeTabPath] || 'Compass';

  const handleNavigate = (path) => {
    if (location.pathname !== path) {
      history.push(path);
    }
  };

  const openMenu = (event) => setMenuAnchor(event.currentTarget);
  const closeMenu = () => setMenuAnchor(null);

  const goToProfile = () => {
    closeMenu();
    history.push('/profile');
  };

  const handleLogout = () => {
    closeMenu();
    logout();
    history.push('/login');
  };

  return (
    <div className="glass-app-shell">
      <header className="glass-app-header">
        <Typography component="h1" className="glass-app-header-title">
          {title}
        </Typography>
        <IconButton
          className="glass-app-avatar-btn"
          onClick={openMenu}
          aria-label="Účet"
          size="small"
        >
          <Avatar className="glass-app-avatar" aria-hidden>
            <Person sx={{ fontSize: 26 }} />
          </Avatar>
        </IconButton>
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={closeMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem onClick={goToProfile}>Profil</MenuItem>
          <MenuItem onClick={handleLogout}>Odhlásit se</MenuItem>
        </Menu>
      </header>

      <main className="glass-app-main">{children}</main>

      <nav className="glass-tab-bar" aria-label="Hlavní navigace">
        {visibleTabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTabPath === tab.path;
          const badge = tab.showUnread && unreadCount > 0 ? unreadCount : 0;

          return (
            <button
              key={tab.path}
              type="button"
              className="glass-tab-bar-item"
              data-active={isActive ? 'true' : 'false'}
              onClick={() => handleNavigate(tab.path)}
              aria-current={isActive ? 'page' : undefined}
            >
              <span className="glass-tab-bar-icon-wrap">
                <Icon sx={{ fontSize: 22 }} />
                {badge > 0 && (
                  <span className="glass-tab-bar-badge">{badge > 99 ? '99+' : badge}</span>
                )}
              </span>
              <span className="glass-tab-bar-label">{tab.shortLabel}</span>
            </button>
          );
        })}
      </nav>
    </div>
  );
};

export default MobileAppShell;
